class TableOfContents extends HTMLElement {
    constructor() {
        super();
        this.usedIds = new Set();
        this._onClick = this.handleClick.bind(this);
        this._onScrollOrResize = this.requestActiveUpdate.bind(this);
        this._scrollTicking = false;
        this._headingElements = [];
        this._linksByHeadingId = new Map();
    }
  
    connectedCallback() {
      this.build();
    }
    disconnectedCallback() {
        this.teardownScrollSpy();
    }

    build() {
      const selector = this.getAttribute('content-selector') || '.hdt-article-block--content';
      const container = document.querySelector(selector);
  
      if (!container) {
        this.style.display = 'none';
        return;
      }
  
      const headings = Array.from(container.querySelectorAll('h2, h3'));
  
      if (!headings.length) {
        this.style.display = 'none';
        return;
      }
  
      this.ensureHeadingIds(headings);
  
      const tree = this.buildTree(headings);
      const list = this.renderTree(tree);
  
      // Render ra DOM
      this.innerHTML = '';
  
      this.appendChild(list);
  
      this.removeEventListener('click', this._onClick);
      this.addEventListener('click', this._onClick);
      
      this.setupScrollSpy(headings);
    }
  
    /**Assign IDs to headings that don't have IDs */
    ensureHeadingIds(headings) {
      headings.forEach((heading) => {
        if (heading.id) {
          this.usedIds.add(heading.id);
          return;
        }
  
        const baseSlug = this.slugify(heading.textContent);
        let finalSlug = baseSlug;
        let counter = 1;
  
        while (this.usedIds.has(finalSlug) || document.getElementById(finalSlug)) {
          finalSlug = `${baseSlug}-${counter}`;
          counter += 1;
        }
  
        heading.id = finalSlug;
        this.usedIds.add(finalSlug);
      });
    }
  
    slugify(text) {
      const vietnameseMap = {
        à: 'a', á: 'a', ạ: 'a', ả: 'a', ã: 'a',
        â: 'a', ầ: 'a', ấ: 'a', ậ: 'a', ẩ: 'a', ẫ: 'a',
        ă: 'a', ằ: 'a', ắ: 'a', ặ: 'a', ẳ: 'a', ẵ: 'a',
        è: 'e', é: 'e', ẹ: 'e', ẻ: 'e', ẽ: 'e',
        ê: 'e', ề: 'e', ế: 'e', ệ: 'e', ể: 'e', ễ: 'e',
        ì: 'i', í: 'i', ị: 'i', ỉ: 'i', ĩ: 'i',
        ò: 'o', ó: 'o', ọ: 'o', ỏ: 'o', õ: 'o',
        ô: 'o', ồ: 'o', ố: 'o', ộ: 'o', ổ: 'o', ỗ: 'o',
        ơ: 'o', ờ: 'o', ớ: 'o', ợ: 'o', ở: 'o', ỡ: 'o',
        ù: 'u', ú: 'u', ụ: 'u', ủ: 'u', ũ: 'u',
        ư: 'u', ừ: 'u', ứ: 'u', ự: 'u', ử: 'u', ữ: 'u',
        ỳ: 'y', ý: 'y', ỵ: 'y', ỷ: 'y', ỹ: 'y',
        đ: 'd',
      };
  
      const normalized = text
        .toLowerCase()
        .trim()
        .split('')
        .map((char) => vietnameseMap[char] || char)
        .join('');
  
      const slug = normalized
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
  
      return slug || 'heading';
    }
  
    /** Bulid ul list */
    buildTree(headings) {
      const root = { level: 1, children: [] };
      const stack = [root];
  
      headings.forEach((heading) => {
        const level = parseInt(heading.tagName.charAt(1), 10);
        const node = { level, heading, children: [] };
  
        while (stack.length > 1 && stack[stack.length - 1].level >= level) {
          stack.pop();
        }
  
        stack[stack.length - 1].children.push(node);
        stack.push(node);
      });
  
      return root.children;
    }
  
    renderTree(nodes) {
      const ul = document.createElement('ul');
      ul.className = 'hdt-toc-list';
  
      nodes.forEach((node) => {
        const li = document.createElement('li');
        li.className = `hdt-toc-item hdt-toc-item--h${node.level}`;
  
        const a = document.createElement('a');
        a.href = `#${node.heading.id}`;
        a.className = 'hdt-toc-link hdt-link';
        a.textContent = node.heading.textContent.trim();
        a.dataset.targetId = node.heading.id;
  
        li.appendChild(a);
  
        if (node.children.length) {
          li.appendChild(this.renderTree(node.children));
        }
  
        ul.appendChild(li);
      });
  
      return ul;
    }
  
    //Heading active
    setupScrollSpy(headings) {
        this.teardownScrollSpy();

        this._headingElements = headings;
        this._linksByHeadingId = new Map();

        headings.forEach((heading) => {
            const link = this.querySelector(`.hdt-toc-link[data-target-id="${heading.id}"]`);
            if (link) this._linksByHeadingId.set(heading.id, link);
        });

        window.addEventListener('scroll', this._onScrollOrResize, { passive: true });
        window.addEventListener('resize', this._onScrollOrResize);

        this.requestActiveUpdate();
    }

    teardownScrollSpy() {
        window.removeEventListener('scroll', this._onScrollOrResize);
        window.removeEventListener('resize', this._onScrollOrResize);
        }

        requestActiveUpdate() {
        if (this._scrollTicking) return;
        this._scrollTicking = true;
        requestAnimationFrame(() => {
            this._scrollTicking = false;
            this.updateActiveHeading();
        });
    }

    updateActiveHeading() {
      if (!this._headingElements.length) return;
  
      const offset = parseInt(this.getAttribute('scroll-offset') || '100', 10);
      let activeHeading = null;
  
      for (const heading of this._headingElements) {
        const rect = heading.getBoundingClientRect();
        if (rect.top - offset <= 1) {
          activeHeading = heading;
        } else {
          break;
        }
      }
  
      const link = activeHeading ? this._linksByHeadingId.get(activeHeading.id) : null;
      this.setActiveLink(link);
    }
  
    setActiveLink(link) {
      this.querySelectorAll('.hdt-toc-link').forEach((el) => el.classList.remove('is-active'));
      if (link) link.classList.add('is-active');
    }


    //Even click
    handleClick(event) {
        const link = event.target.closest('.hdt-toc-link');
        if (!link) return;
    
        event.preventDefault();
    
        const targetId = link.dataset.targetId;
        const target = document.getElementById(targetId);
        if (!target) return;
    
        const offset = parseInt(this.getAttribute('scroll-offset') || '100', 10);
        const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
    
        window.scrollTo({ top, behavior: 'smooth' });
    
        if (history.pushState) {
          history.pushState(null, '', `#${targetId}`);
        }
    
        this.setActiveLink(link);
    }
  }
  
  if (!customElements.get('table-of-contents')) {
    customElements.define('table-of-contents', TableOfContents);
  }